import React, { useEffect, useRef, useState, useCallback} from 'react';
import { usePayments } from '../../context/PaymentContext';
import { Link } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import Pagination from '../common/Pagination';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/AuthContext';
import { useRoutePath } from '../../utils/getRoutePath';
import Breadcrumb from '../common/Breadcrumb';
import { formatCurrency } from '../../utils/format';

const PaymentList = () => {
  const { payments, deletePayment, getPayments } = usePayments();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const { user } = useAuth();
  const [showAddBtn, setShowAddBtn] = useState(true);
  const [showDeleteBtn, setShowDeleteBtn] = useState(true);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const getRoutePath = useRoutePath();
  const [totalPages, setTotalPages] = useState(1);
  const paymentsRef = useRef(false);
  const [sortConfig, setSortConfig] = useState({
    key: 'created_at',
    direction: 'desc'
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const page = parseInt(params.get('page')) || 1;
    setCurrentPage(page);
    if (user.role_id !== 1 && payments?.data?.length >= 1) {
      setShowAddBtn(true);
    }
    if (user.role_id !== 1) {
      setShowDeleteBtn(true);
    }
    setSearchTerm(params.get('q') || '');
    setStatusFilter(params.get('active') || '');
      
  }, [user, payments, showAddBtn]);

  const filtersData = (key, value, arrayFilters = []) => {
    const applyFilters = async (filterDataVal) => {
      let filters = {};
      
      const newUrl = new URL(window.location);
      if (filterDataVal['q']?.key === 'q') {
        filters.q = filterDataVal['q']?.value;
        if (filterDataVal['q']?.value) {
          setSearchTerm(filterDataVal['q'].value);
          newUrl.searchParams.set('q', filterDataVal['q'].value);
        } else {
          newUrl.searchParams.delete('q');
        }
      } else if (searchTerm) {
        filters.q = searchTerm;
        newUrl.searchParams.set('q', searchTerm);
      } else {
        newUrl.searchParams.delete('q');
      }

      if (filterDataVal['active']?.key === 'active') {
        filters.active = filterDataVal['active']?.value;
        if (filterDataVal['active']?.value) {
          setStatusFilter(filterDataVal['active'].value);
          newUrl.searchParams.set('active', filterDataVal['active'].value);
        } else {
          newUrl.searchParams.delete('active');
        }
      } else if (statusFilter) {
        filters.active = statusFilter;
        newUrl.searchParams.set('active', statusFilter);
      } else {
        newUrl.searchParams.delete('active');
      }
      newUrl.searchParams.delete('page');
      window.history.pushState({}, '', newUrl);

      const params = new URLSearchParams(window.location.search);
      const page = parseInt(params.get('page')) || 1;
      if (Object.keys(filters).length > 0) {
        let response = await getPayments(page, filters);
        if (response?.data) {
          setTotalPages(response?.last_page ? response.last_page : 1);
          setFilteredPayments(response?.data);
          
        }
      }
    };

    let filterDataVal = {};
    if (arrayFilters.length > 0) {
      arrayFilters.forEach(filter => {
        filterDataVal[filter.key] = {
          key: filter.key, value: filter.value
        };
      });
    } else {
      filterDataVal[key] = {
        key: key, value: value
      };
    }
   
    applyFilters(filterDataVal);
  };

  const fetchPayments = useCallback(async (page = 1, filters = {}) => {
     
      let response = await getPayments(page, filters);
      if (response?.data) {
        setTotalPages(response?.last_page ?? 1);
        setFilteredPayments(response?.data);
         
      }
       
  }, [getPayments]); // ✅ include dependencies used inside

  useEffect(() => {
    let filters = {};
    if (searchTerm) filters.q = searchTerm;
    if (statusFilter) filters.active = statusFilter;
    if (sortConfig.key) filters.sort = sortConfig.key;
    const params = new URLSearchParams(window.location.search);
    const page = parseInt(params.get('page')) || 1;
    filters.page = page;
  
    fetchPayments(page,filters)
     
    
  }, [searchTerm, statusFilter, sortConfig.key,fetchPayments,paymentsRef,currentPage]); // ✅ no warning now

  
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const currentItems = filteredPayments;
  const handlePageChange = (page) => {
    
    setCurrentPage(page);
  
    let filters = {};
    if (searchTerm) filters.q = searchTerm;
    if (statusFilter) filters.active = statusFilter;
    if (sortConfig.key) filters.sort = sortConfig.key;
    filters.page = page
    
    fetchPayments(page,filters)
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('page', page);
    window.history.pushState({}, '', newUrl);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        deletePayment(id).then(response => {
          if (response.status === 200) {
            Swal.fire('Deleted!', 'Payment has been deleted.', 'success');
          } else {
            Swal.fire('Error!', 'Payment has not been deleted.', 'error');
          }
        });
      }
    });
  };

  const SortableHeader = ({ columnKey, children }) => (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
      onClick={() => handleSort(columnKey)}
    >
      <div className="flex items-center">
        {children}
        {sortConfig.key === columnKey && (
          <span className="ml-1">
            {sortConfig.direction === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
    </th>
  );

  return (
    <div className="py-6">
      <div className="max-w-10xl mx-auto px-4 sm:px-6 md:px-8">
        <Breadcrumb />
        <div className="mb-3">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-900">Payments</h1>
            {showAddBtn && (
              <Link
                to={getRoutePath('/payments/create')}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb]"
              >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add New Payment
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-10xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="bg-white rounded-lg shadow-md mb-3">
          <div className="mb-4 grid grid-cols-1 md:grid-cols-2 p-5 gap-4">
            <div>
              <input
                type="text"
                placeholder="Search payments..."
                value={searchTerm}
                onChange={(e) => filtersData('q', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Payment Details</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">Payment information and details.</p>
          </div>
          <div className="border-t border-gray-200">
            <div className="overflow-x-auto scrollbar-thin">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortableHeader columnKey="created_at">Payment Date</SortableHeader>
                    <SortableHeader columnKey="payment_number">Payment #</SortableHeader>
                    <SortableHeader columnKey="payment_number">Invoice #</SortableHeader>
                    <SortableHeader columnKey="payment_method.name">Payment Method</SortableHeader>
                    <SortableHeader columnKey="total_amount">Total Amount</SortableHeader>
                    <SortableHeader columnKey="amount">Amount</SortableHeader>
                    <SortableHeader columnKey="amount_due">Amount Due</SortableHeader>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentItems.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="px-6 py-4 text-center text-gray-500">
                        <div className="flex flex-col items-center justify-center space-y-4">
                          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                            <PlusIcon className="w-8 h-8 text-gray-400" />
                          </div>
                          <div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Payments Found</h3>
                            <p className="text-gray-500 mb-4">Get started by adding a new payment</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((payment) => (
                      <tr key={payment.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{payment?.formated_created_at || payment?.created_at || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            <Link to={getRoutePath(`/payments/view/${payment?.id}`)} className="text-[#009efb] hover:text-[#0089db]">
                              {payment?.payment_number || payment?.id || 'N/A'}
                            </Link>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            <Link to={getRoutePath(`/invoices/view/${payment?.invoice?.id}`)} className="text-[#009efb] hover:text-[#0089db]">
                              {payment?.invoice?.invoice_number || 'N/A'}
                            </Link>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{payment?.payment_method?.name || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatCurrency(Number(payment?.total).toFixed(2))}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-green-500 font-medium ">${payment?.amount ? parseFloat(payment?.amount).toFixed(2) : '0.00'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">${payment?.amount_due ? parseFloat(payment?.amount_due).toFixed(2) : '0.00'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link
                            to={getRoutePath(`/payments/view/${payment.id}`)}
                            className="text-[#009efb] hover:text-[#0089db] mr-4"
                            title="View"
                          >
                            <EyeIcon className="w-4 h-4 inline" />
                          </Link>
                          <Link
                            to={getRoutePath(`/payments/edit/${payment.id}`)}
                            className="text-[#009efb] hover:text-[#0089db] mr-4"
                            title="Edit"
                          >
                            <PencilIcon className="w-4 h-4 inline" />
                          </Link>
                          {showDeleteBtn && (
                            <button
                              onClick={() => handleDelete(String(payment.id))}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <TrashIcon className="w-4 h-4 inline" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {currentItems.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-200">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentList;


